import type { ApolloClient } from '@apollo/client';

import { GET_SCOPED_PRODUCT_SALES_FOR_PRODUCTS } from '../apollo/queries/productSalesBulk';
import type { PriceSurvey } from '../codegen/graphql';
import { getLastCompletedFinancialWeekSpan } from './financialWeek';
import type { ProductSalesScopeInput } from '../hooks/useProductSalesScope';

export async function prefetchSurveyProductSales(
    client: ApolloClient<unknown>,
    survey: Pick<PriceSurvey, 'dueBy' | 'surveyItems' | 'advertisingZoneId'>,
): Promise<void> {
    const productIds = [
        ...new Set(
            (survey.surveyItems ?? [])
                .map(item => item.product?.id ?? item.productId ?? '')
                .filter((id): id is string => !!id),
        ),
    ];
    if (productIds.length === 0 || !survey.dueBy) {
        return;
    }

    const dueBy = new Date(survey.dueBy);
    if (Number.isNaN(dueBy.getTime())) {
        return;
    }
    const { thisYear, lastYear } = getLastCompletedFinancialWeekSpan(dueBy);
    const thisYearStart = thisYear.startDate;
    const thisYearEnd = thisYear.endDate;
    const lastYearStart = lastYear.startDate;
    const lastYearEnd = lastYear.endDate;

    const advertisingZoneId = survey.advertisingZoneId?.trim();
    const scope: ProductSalesScopeInput | undefined = advertisingZoneId
        ? { advertisingZoneId }
        : undefined;
    await Promise.all([
        client.query({
            query: GET_SCOPED_PRODUCT_SALES_FOR_PRODUCTS,
            variables: {
                productIds,
                startDate: thisYearStart,
                endDate: thisYearEnd,
                scope,
            },
            fetchPolicy: 'cache-first',
        }),
        client.query({
            query: GET_SCOPED_PRODUCT_SALES_FOR_PRODUCTS,
            variables: {
                productIds,
                startDate: lastYearStart,
                endDate: lastYearEnd,
                scope,
            },
            fetchPolicy: 'cache-first',
        }),
    ]);
}
